import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import {
    AlaStatus,
    Application,
    ApplicationDto,
    ApplicationJournalRecordDto,
    ApplicationServiceProxy,
    ApplicationStatus,
    ApplicationSupplementalClaimDto,
    ApplicationType,
    ApplicationUploadFilesInput,
    AssignApplicationInput,
    AssignApplicationOutput,
    ClaStatus,
    CogsStatus,
    CreateApplicationJournalRecordInput,
    EhcStatus,
    EvaluationDto,
    EvaluationInput,
    GeneralDocumentGroupDto,
    GeneralDocumentsOutput,
    GeneralFileDto,
    GeneralFileInfoDto,
    GeneralResultDocumentInput,
    LegStatus,
    LiaStatus,
    MutStatus,
    NdnStatus,
    NiStatus,
    NotConsiderApplicationInputDto,
    NpboStatus,
    NpfStatus,
    PersonApplicationSearchDto,
    RequestForSupplementalInformationInput,
    RlaStatus,
    SetSupplementalInformationReminderDateInput,
    SlapStatus,
    SplStatus,
    VacPharmStatus,
    WithdrawApplicationInputDto
} from '@shared/service-proxies/service-proxies';
import { AppConsts } from '@shared/AppConsts';
import { AppComponentBase } from '@shared/common/app-component-base';
import { CodeService } from '@app/shared/common/code/code.service';
import { SelectItem } from 'primeng/api';
import { DateTime } from 'luxon';
import { ClaService } from '../services/cla.service';
import { LiaService } from '../services/lia.service';
import { EhcService } from '../services/ehc.service';
import { SlapService } from '../services/slap.service';
import { AlaService } from '../services/ala.service';
import { SplService } from '../services/spl.service';
import { MutService } from '../services/mut.service';
import { LegService } from '../services/leg.service';
import { NdqrService } from '../services/ndqr.service';
import { CogsService } from '../services/cogs.service';
import { RlaService } from '../services/rla.service';
import { NpboService } from '../services/Npbo.service';
import { NdnService } from '../services/ndn.service';
import { NpfService } from '../services/npf.service';
import { NiService } from '../services/ni.service';
import { VacPharmService } from '../services/vac-pharm.service';

@Injectable({ providedIn: 'root' })
export class ApplicationService extends AppComponentBase {

    private evaluationChangedSource = new ReplaySubject<void>(1);
    public evaluationChanged$ = this.evaluationChangedSource.asObservable();

    private applicationStatusSource = new BehaviorSubject<ApplicationStatus>(undefined);
    applicationStatus$ = this.applicationStatusSource.asObservable();

    private journalsSubject = new BehaviorSubject<ApplicationJournalRecordDto[]>([]);
    journals$ = this.journalsSubject.asObservable();

    protected _application: ApplicationDto;
    protected _caseId = '';
    applicationStatus: SelectItem[];

    constructor(
        injector: Injector,
        protected codeService: CodeService,
        private applicationService: ApplicationServiceProxy,
    ) {
        super(injector);
    }

    get isAuthority() {
        return this.permission.isGranted('Pages.Authority.Applications');
    }

    areAllMandatoryDocumentsUploaded(documents: GeneralDocumentGroupDto[]) {
        if (documents == null) {
            return false;
        }

        for (let i = 0; i < documents.length; i++) {
            if (documents[i].isMandatory && documents[i].files.length === 0) {
                return false;
            }
        }
        return true;
    }

    getApplication(caseId: string): Observable<ApplicationDto> {
        if (this._caseId !== caseId){
            this.journalsSubject.next([]);
        }
        this._caseId = caseId;
        return this.applicationService.getApplication(caseId).pipe(tap((output: ApplicationDto) => {
            this.application = output;
        }));
    }

    closeApplication(): void {
        this.application = null;
        this._caseId = '';
    }

    get application(): ApplicationDto {
        return this._application;
    }

    set application(value: ApplicationDto) {
        this._application = value;
        this.applicationStatusSource.next(this._application?.status);
    }

    deleteApplication(): Observable<void> {
        return this.applicationService.deleteApplication(this._caseId);
    }

    isInStatus(expectedStatus: ApplicationStatus): boolean {
        return this._application.status === expectedStatus;
    }

    static isOpen(state: ApplicationStatus): boolean {
        switch (state) {
            case ApplicationStatus.UnderConstruction:
            case ApplicationStatus.Submitted:
            case ApplicationStatus.UnderPreEvaluation:
            case ApplicationStatus.UnderEvaluation:
            case ApplicationStatus.SupplementalClaim:
            case ApplicationStatus.Waitlist:
                return true;
            case ApplicationStatus.FormallyEvaluated:
            case ApplicationStatus.Withdrawn:
            case ApplicationStatus.NotConsidered:
            default:
                return false;
        }
    }

    get status(): ApplicationStatus {
        return this._application?.status;
    }

    set status(value: ApplicationStatus) {
        this._application.status = value;
        this.applicationStatusSource.next(value);
    }

    get caseId(): string {
        return this._caseId;
    }

    get type(): ApplicationType {
        return this._application?.type;
    }

    getReleaseComment(): Observable<any> {
        return of(this._application.applicantComment);
    }

    assign(input: AssignApplicationInput): Observable<AssignApplicationOutput> {
        return this.applicationService.assignApplication(input);
    }

    unassign(caseId: string): Observable<void> {
        return this.applicationService.unassignApplication(caseId);
    }

    withdrawnApplication(input: WithdrawApplicationInputDto): Observable<void> {
        return this.applicationService.withdrawApplication(this.caseId, '', input).pipe(tap(() => {
            this.status = ApplicationStatus.Withdrawn;
        }));
    }

    withdrawnCombinedApplication(input: WithdrawApplicationInputDto, ehcCaseId: string): Observable<void> {
        return this.applicationService.withdrawApplication(this.caseId, ehcCaseId, input).pipe(tap(() => {
            this.status = ApplicationStatus.Withdrawn;
        }));
    }

    reactivateApplication(caseId: string = this._caseId): Observable<void> {
        return this.applicationService.reactivateApplication(caseId).pipe(tap(() => {
            this.status = ApplicationStatus.Submitted;
        }));
    }

    notConsiderApplication(input: NotConsiderApplicationInputDto): Observable<void> {
        return this.applicationService.notConsiderApplication(this.caseId, input).pipe(tap(() => {
            this.status = ApplicationStatus.NotConsidered;
        }));
    }

    startPreEvaluation(caseId: string): Observable<void> {
        return this.applicationService.startPreEvaluation(caseId).pipe(tap(() => {
            this.status = ApplicationStatus.UnderPreEvaluation;
        }));
    }

    startEvaluation(caseId: string): Observable<void> {
        return this.applicationService.startEvaluation(caseId).pipe(tap(() => {
            this.status = ApplicationStatus.UnderEvaluation;
        }));
    }

    getEvaluation(step: number): EvaluationDto {
        if ((this._application.status !== ApplicationStatus.UnderPreEvaluation && this._application.status !== ApplicationStatus.UnderEvaluation)
            || this.application.evaluations == null) {
            return null;
        }

        return this.application.evaluations.find(e => e.step === step);
    }

    getEvaluationForSupplementalClaim(step: number): EvaluationDto {
        if (this._application.status !== ApplicationStatus.SupplementalClaim) {
            return null;
        }

        return this.application.evaluations.find(e => e.step === step);
    }

    getSupplementalClaimTemplate(): string {
        return this.application.emailTemplate.supplementalClaimTemplate;
    }

    reviewActionPossible(): boolean {
        switch (this._application.status) {
            case ApplicationStatus.FormallyEvaluated:
            case ApplicationStatus.NotConsidered:
            case ApplicationStatus.Withdrawn:
            case ApplicationStatus.Submitted:
                return false;
            default:
                return true;
        }
    }

    getEvaluations(): EvaluationDto[] {
        if (this._application.status !== ApplicationStatus.UnderPreEvaluation && this._application.status !== ApplicationStatus.UnderEvaluation) {
            return null;
        }

        return this.application.evaluations;
    }

    isEvaluationFinished(): boolean {
        if (this._application.evaluations == null) {
            return false;
        }

        return this.application.evaluations.every(x => x.okayId === AppConsts.codes.evaluationStatus.ok ||
            x.okayId === AppConsts.codes.evaluationStatus.notOk ||
            x.okayId === AppConsts.codes.evaluationStatus.internalClarificationRequired);
    }

    isUnderConstruction(): boolean {
        switch (this.application?.status) {
            case ApplicationStatus.UnderConstruction:
              return true;
            default:
              return false;
            }
    }

    isUnderPreEvaluation(): boolean {
        return this.application?.status === ApplicationStatus.UnderPreEvaluation;
    }

    isUnderEvaluation(): boolean {
        return this.application?.status === ApplicationStatus.UnderEvaluation;
    }

    canNotConsider(): boolean {
        if (this.isAuthority) {
            switch (this.application?.status) {
              case ApplicationStatus.UnderPreEvaluation:
              case ApplicationStatus.UnderEvaluation:
              case ApplicationStatus.SupplementalClaim:
              case ApplicationStatus.Waitlist:
                return true;
              default:
                return false;
            }
        }

        return false;
    }

    canReactivate(): boolean {
        if (this.isAuthority) {
            switch (this.application?.status) {
              case ApplicationStatus.NotConsidered:
              case ApplicationStatus.Withdrawn:
                return true;
              default:
                return false;
            }
        }

        return false;
    }

    canWithdraw(): boolean {
        switch (this.application?.status) {
            case ApplicationStatus.UnderConstruction:
            case ApplicationStatus.Submitted:
            case ApplicationStatus.Waiting:
            case ApplicationStatus.Waitlist:
                return true;
            default:
                break;
        }

        if (this.isAuthority) {
            switch (this.application?.status as ApplicationStatus) {
                case ApplicationStatus.UnderPreEvaluation:
                case ApplicationStatus.UnderEvaluation:
                case ApplicationStatus.Waitlist:
                  return true;
                  default:
                break;
            }
        }
        return false;
    }

    canRelease(): boolean {
        return this.status === ApplicationStatus.UnderConstruction || this.status === ApplicationStatus.SupplementalClaim;
    }

    updateEvaluation(evaluation: EvaluationDto): Observable<void> {
        return this.applicationService.updateEvaluation(new EvaluationInput({ caseId: this._application.caseId, evaluation: evaluation })).pipe(tap(() => {
            const item = this._application.evaluations.find(e => e.step === evaluation.step);
            const index = this._application.evaluations.indexOf(item);
            this._application.evaluations[index] = evaluation;
            const okay = this.isFormallyOkay();
            this.evaluationChangedSource.next();
        }));
    }

    requestForSupplementalInformation(message: string, isReminder: boolean = false): Observable<ApplicationDto> {
        return this.applicationService.requestForSupplementalInformation(new RequestForSupplementalInformationInput({ caseId: this._application.caseId, message: message, isReminder: isReminder }))
            .pipe(tap((app) => {
                this.application = app;
            }));
    }

    setSupplementalInformationReminderDate(reminderDate: DateTime | Date): Observable<ApplicationDto> {
        reminderDate = DateTime.isDateTime(reminderDate) ? reminderDate : DateTime.fromJSDate(reminderDate);

        return this.applicationService.setSupplementalInformationReminderDate(new SetSupplementalInformationReminderDateInput({
            caseId: this.caseId,
            reminderDate: reminderDate
        }))
        .pipe(tap((app) => {
            this.application = app;
        }));
    }

    getCurrentSupplementalClaim(): ApplicationSupplementalClaimDto {
        // The backend sorts them already
        const dto = this._application.supplementaryClaims.at(0);
        return dto;
    }

    hasUndeliveredSupplementalClaims(): boolean {
        return (this._application.status === ApplicationStatus.UnderPreEvaluation || this._application.status === ApplicationStatus.UnderEvaluation) && this.isFormallyNotOkay();
    }

    reviseApplication(): Observable<void> {
        return this.applicationService.setApplicationUnderConstruction(this._application.caseId);
    }

    isFormallyOkay(): boolean {
        return this._application.evaluations?.filter(e => e.okayId !== AppConsts.codes.evaluationStatus.ok).length === 0;
    }

    isFormallyOkayOrInternalClarificationRequired(): boolean {
        return this._application.evaluations?.filter(e =>
            e.okayId !== AppConsts.codes.evaluationStatus.ok &&
            e.okayId !== AppConsts.codes.evaluationStatus.internalClarificationRequired).length === 0;
    }

    anyStepInternalClarificationRequired(): boolean {
        return this._application.evaluations?.filter(e =>
            e.okayId === AppConsts.codes.evaluationStatus.internalClarificationRequired).length > 0;
    }

    anyStepNotOkay(): boolean {
        return this._application.evaluations?.filter(e =>
            e.okayId === AppConsts.codes.evaluationStatus.notOk).length > 0;
    }

    isFormallyNotOkay(): boolean {
        return this._application.evaluations?.filter(e =>
            e.okayId === AppConsts.codes.evaluationStatus.notOk ||
            e.okayId === AppConsts.codes.evaluationStatus.internalClarificationRequired).length > 0;
    }

    uploadResultDocument(input: GeneralResultDocumentInput): Observable<void> {
        return this.applicationService.uploadResultDocument(input);
    }

    private checkOverallEvaluationStatus(oldOkay: boolean) {
        const okay = this.isFormallyOkay();
        if (okay !== oldOkay) {
        }
    }

    transferToDossier(): Observable<void> {
        return this.applicationService.formallyEvaluateApplication(this._application.caseId);
    }

    accept(): Observable<void> {
        return this.applicationService.formallyEvaluateApplication(this._application.caseId);
    }

    getUrlForTypeAndStep(type: ApplicationType, step: number, firstStep: boolean = false): string {
        if (this.type === ApplicationType.Lia || this.type === ApplicationType.LiaRen || this.type === ApplicationType.Lia90Days) {
            return firstStep ? LiaService.getUrlForStep(LiaStatus.Person) : LiaService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Ehc || this.type === ApplicationType.Ehc90Days) {
            return firstStep ? EhcService.getUrlForStep(EhcStatus.SelfEmployment) : EhcService.getUrlForStep(step);
        } else if (this.type === ApplicationType.EhcRen) {
            return firstStep ? EhcService.getUrlForStep(EhcStatus.Register) : EhcService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Slap || this.type === ApplicationType.SlapRen) {
            return firstStep ? SlapService.getUrlForStep(SlapStatus.Person) : SlapService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Alap) {
            return firstStep ? AlaService.getUrlForStep(AlaStatus.ApplicantPerson) : AlaService.getUrlForStep(step);
        } else if (this.type === ApplicationType.AlapExtr) {
            return firstStep ? AlaService.getUrlForStep(AlaStatus.ApplicantPersonExtr) : AlaService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Tla) {
            return firstStep ? null : null; // this.getUrlForStep(step);
        } else if (this.type === ApplicationType.Rla || this.type === ApplicationType.RlaLim) {
            return firstStep ? RlaService.getUrlForStep(RlaStatus.ApplicantPerson) : RlaService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Abo) {
            return firstStep ? SplService.getUrlForStep(SplStatus.Person) : SplService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Sub) {
            return firstStep ? SplService.getUrlForStep(SplStatus.Person) : SplService.getUrlForStep(step);
        } else if ((this.type === ApplicationType.Cla ||
            this.type === ApplicationType.ClaEhc ||
            this.type === ApplicationType.ClaEhcRen ||
            this.type === ApplicationType.ClaRen ||
            this.type === ApplicationType.Ola ||
            this.type === ApplicationType.OlaRen ||
            this.type === ApplicationType.Slas ||
            this.type === ApplicationType.SlasRen ||
            this.type === ApplicationType.Nla ||
            this.type === ApplicationType.NlaRen ||
            this.type === ApplicationType.Bla ||
            this.type === ApplicationType.BlaRen ||
            this.type === ApplicationType.Alas)) {
            return firstStep ? ClaService.getUrlForStep(ClaStatus.Sponsorship) : ClaService.getUrlForStep(step);
        }else if (this.type === ApplicationType.Mut) {
            return firstStep ? null : MutService.getUrlForStep(step);
        } else if (this.type === ApplicationType.LegPerson) {
            return firstStep ? LegService.getUrlForStep(LegStatus.Person) : LegService.getUrlForStep(step);
        } else if (this.type === ApplicationType.LegSponsorship) {
            return firstStep ? LegService.getUrlForStep(LegStatus.Sponsorship) : LegService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Ndqr) {
            return firstStep ? null : NdqrService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Cogs) {
            return firstStep ? CogsService.getUrlForStep(CogsStatus.Person) : CogsService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Npbo) {
            return firstStep ? NpboService.getUrlForStep(NpboStatus.Register) : NpboService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Ndn) {
            return firstStep ? NdnService.getUrlForStep(NdnStatus.Person) : NdnService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Npf) {
            return firstStep ? NpfService.getUrlForStep(NpfStatus.Contact) : NpfService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Nines ||
                   this.type === ApplicationType.NinesRen ||
                   this.type === ApplicationType.Nifsr ||
                   this.type === ApplicationType.Nipc ||
                   this.type === ApplicationType.Nicis) {
            return firstStep ? NiService.getUrlForStep(NiStatus.Person) : NiService.getUrlForStep(step);
        } else if (this.type === ApplicationType.Nito) {
            return firstStep ? NiService.getUrlForStep(NiStatus.Sponsorship) : NiService.getUrlForStep(step);
        } else if (this.type === ApplicationType.VacPharm) {
            return firstStep ? VacPharmService.getUrlForStep(VacPharmStatus.Person) : VacPharmService.getUrlForStep(step);
        } else if (this.type === ApplicationType.LiaBrg) {
            return firstStep ? LiaService.getUrlForStep(LiaStatus.Person) : LiaService.getUrlForStep(step);
        }

        console.error(`getUrlForTypeAndStep not configured for ${type}`);
        return null;
    }

    getNextEvaluationStepUrl(evaluation: EvaluationDto): string {
        if (this.isEvaluationFinished() && (this.application.status === ApplicationStatus.UnderPreEvaluation || this.application.status === ApplicationStatus.UnderEvaluation)) {
            return this.getFullCaseUrl() + 'wizard/' + this.getUrlForTypeAndStep(this.type, LiaStatus.FinishReview);
        }

        for (let index = 0; index < this._application.evaluations.length; index++) {
            if ((evaluation === null || this._application.evaluations[index].order > evaluation.order) &&
                (!this._application.evaluations[index].okayId || this._application.evaluations[index].okayId !== AppConsts.codes.evaluationStatus.ok)) {
                return this.getFullCaseUrl() + 'wizard/' + this.getUrlForTypeAndStep(this.type, this._application.evaluations[index].step);
            }
        }

        // Return first step
        if (evaluation === null) {
            return this.getFullCaseUrl() + 'wizard/' + this.getUrlForTypeAndStep(this.type, null, true);
        }

        return null;
    }

    getResultDocuments(caseId = this._caseId): Observable<GeneralFileInfoDto[]> {
        return this.applicationService.getResultDocuments(caseId).pipe(map((output) => output.resultDocuments));
    }

    getResultFile(caseId: string, id: number): Observable<GeneralFileDto> {
        return this.applicationService.getResultFile(caseId, id);
    }

    getHistoryStatusDocument(caseId: string, id: number): Observable<GeneralFileDto> {
        return this.applicationService.getApplicationHistoryStatusDocument(caseId, id);
    }

    getApplicationAuthorityHistoryDocument(caseId: string, id: number): Observable<GeneralFileDto> {
        return this.applicationService.getApplicationAuthorityHistoryDocument(caseId, id);
    }

    deleteResultDocument(docId: number): Observable<void> {
        return this.applicationService.deleteResultDocument(this.caseId, docId);
    }

    static getCssClassForStatusId(statusId: ApplicationStatus, prefix: string): string {
        if (statusId === ApplicationStatus.FormallyEvaluated) {
            return prefix + 'success';
        } else if (statusId === ApplicationStatus.Withdrawn || statusId === ApplicationStatus.NotConsidered) {
            return prefix + 'secondary';
        } else if (statusId === ApplicationStatus.SupplementalClaim) {
            return prefix + 'warning';
        }

        return prefix + 'secondary';
    }

    getStatusDescription(statusId: ApplicationStatus) {
        if (!this.applicationStatus) {
            this.applicationStatus = this.codeService.getEnumForDropdown('ApplicationStatus', ApplicationStatus, false);
        }

        if (!abp.auth.isGranted('Pages.Authority.Applications') &&
            (statusId === ApplicationStatus.UnderEvaluation || statusId === ApplicationStatus.UnderPreEvaluation)) {
            return this.l('MySironaLiaApplicationStatusUnderEvaluation');
        } else {
            return this.le('applicationStatus', statusId);
        }
    }

    static displaySubmittedCount(x: ApplicationDto | PersonApplicationSearchDto): string {
        if (x.status === ApplicationStatus.Submitted && x.submittedCount > 1) {
            return `(${x.submittedCount})`;
        }
        return '';
    }

    setStepToReview(stepId: number) {
        // update warning mark from supplemental claim to okay
        if (this._application.status === ApplicationStatus.SupplementalClaim) {
            const step = this._application.evaluations.find(x => x.step === stepId);
            if (step.okayId === AppConsts.codes.evaluationStatus.ok ||
                step.okayId === AppConsts.codes.evaluationStatus.notOk ||
                step.okayId === AppConsts.codes.evaluationStatus.internalClarificationRequired) {
                this.applicationService.setStepToReview(this._application.caseId, stepId).subscribe(() => {
                    const step = this._application.evaluations.find(x => x.step === stepId);
                    step.okayId = null;
                });
            }
        }
    }

    getSupplementalClaimAnnexFilesOfStep(applicationWizardStep: number, caseId = this._caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.applicationService.getSupplementalClaimAnnexFilesOfStep(caseId, applicationWizardStep).pipe(tap((output: GeneralDocumentsOutput) => {
            this.application.authorityDocuments = output.documents;
        })).pipe(map((output: GeneralDocumentsOutput) => output.documents));
    }

    getAuthorityFile(fileId: number): Observable<GeneralFileDto> {
        return this.applicationService.getAuthorityFile(this._caseId, fileId);
    }

    uploadAuthorityFiles(input: ApplicationUploadFilesInput, wizardStep: number): Observable<GeneralFileInfoDto[]> {
        input.caseId = this._caseId;
        return this.applicationService.uploadAuthorityFiles(wizardStep, input);
    }

    deleteAuthorityFile(fileId: number): Observable<void> {
        return this.applicationService.deleteAuthorityFile(fileId, this._caseId);
    }

    deleteAuthorityFiles(wizardStep: number): Observable<void> {
        return this.applicationService.deleteAuthorityFiles(wizardStep, this._caseId);
    }

    setApplicationsInSubmitted(caseIds: string[]): Observable<void> {
        return this.applicationService.setApplicationsInSubmitted(caseIds);
    }

    getJournalRecords(caseId: string) {
        this.applicationService.getJournalRecords(caseId).subscribe(values => this.journalsSubject.next(values));
    }

    createJournalRecord(input: CreateApplicationJournalRecordInput): Observable<ApplicationJournalRecordDto> {
        let resultObservable = this.applicationService.createJournalRecord(input).pipe(shareReplay(1));
        resultObservable.subscribe(result => {
            this.journalsSubject.next([...this.journalsSubject.value, result]);
        });
        return resultObservable;
    }

    deleteJournalRecord(caseId: string, recordId: number) {
        this.applicationService.deleteJournalRecord(caseId, recordId).subscribe(() => {
                    this.journalsSubject.next(this.journalsSubject.value.filter((x) => x.id !== recordId));
                });
    }

    getNextStep(path: string): string {
        const url = this.getFullCaseUrl() + 'wizard/' + path;
        return url;
    }

    getFullApplicationBaseUrl(): string {
        let url;
        switch (this.type) {
            case ApplicationType.Cla:
            case ApplicationType.ClaRen:
            case ApplicationType.Ola:
            case ApplicationType.OlaRen:
            case ApplicationType.Nla:
            case ApplicationType.NlaRen:
            case ApplicationType.Bla:
            case ApplicationType.BlaRen:
            case ApplicationType.Slas:
            case ApplicationType.SlasRen:
            case ApplicationType.ClaEhc:
            case ApplicationType.ClaEhcRen:
            case ApplicationType.Alas:
                url = this.isAuthority ? AppConsts.authorityBaseUrl + '/applications/sponsorship' : AppConsts.applicantBaseUrl;
                break;
            case ApplicationType.Mut:
                url = this.isAuthority ? AppConsts.authorityBaseUrl + '/applications/mutation' : AppConsts.applicantBaseUrl;
                break;
            case ApplicationType.LegPerson:
            case ApplicationType.LegSponsorship:
                url = this.isAuthority ? AppConsts.authorityBaseUrl + '/applications/legitimations' : AppConsts.applicantBaseUrl;
                break;
            case ApplicationType.Lia:
            case ApplicationType.LiaRen:
            case ApplicationType.Lia90Days:
            case ApplicationType.Ehc:
            case ApplicationType.Ehc90Days:
            case ApplicationType.EhcRen:
            case ApplicationType.Slap:
            case ApplicationType.SlapRen:
            case ApplicationType.Alap:
            case ApplicationType.AlapExtr:
            case ApplicationType.Tla:
            case ApplicationType.Rla:
            case ApplicationType.Abo:
            case ApplicationType.Sub:
            case ApplicationType.Npbo:
            case ApplicationType.Npf:
            default:
                url = this.isAuthority ? AppConsts.authorityBaseUrl + '/applications/person' : AppConsts.applicantBaseUrl;
        }

        return url;
    }

    getFullCaseUrl(): string {
        const url = this.getFullApplicationBaseUrl() + `/${ApplicationService.getNavigationContext(this.type)}/${this.caseId}/`;
        return url;
    }

    static getNavigationContext(type: ApplicationType): string {
        switch (type) {
            case ApplicationType.Lia:
            case ApplicationType.LiaRen:
            case ApplicationType.Lia90Days:
            case ApplicationType.LiaBrg:
                return 'lia';
            case ApplicationType.Ehc:
            case ApplicationType.EhcRen:
            case ApplicationType.Ehc90Days:
                return 'ehc';
            case ApplicationType.Rla:
            case ApplicationType.RlaLim:
                return 'rla';
            case ApplicationType.Alap:
            case ApplicationType.AlapExtr:
                return 'ala';
            case ApplicationType.Tla:
                return 'tla';
            case ApplicationType.Slap:
            case ApplicationType.SlapRen:
                return 'slap';

            case ApplicationType.Cla:
            case ApplicationType.ClaRen:
            case ApplicationType.Slas:
            case ApplicationType.SlasRen:
            case ApplicationType.Ola:
            case ApplicationType.OlaRen:
            case ApplicationType.Nla:
            case ApplicationType.NlaRen:
            case ApplicationType.Bla:
            case ApplicationType.BlaRen:
            case ApplicationType.Alas:
            case ApplicationType.ClaEhc:
            case ApplicationType.ClaEhcRen:
                return 'cla';
            case ApplicationType.Abo:
            case ApplicationType.Sub:
                return 'spl';

            case ApplicationType.Mut:
                return 'mut';
            case ApplicationType.Ndqr:
                return 'ndqr';
            case ApplicationType.LegPerson:
            case ApplicationType.LegSponsorship:
                return 'leg';
            case ApplicationType.Ndqr:
                return 'ndqr';
            case ApplicationType.Cogs:
                return 'cogs';
            case ApplicationType.Npbo:
                return 'npbo';
            case ApplicationType.Ndn:
                return 'ndn';
            case ApplicationType.Npf:
                return 'npf';
            case ApplicationType.Nines:
            case ApplicationType.NinesRen:
            case ApplicationType.Nifsr:
            case ApplicationType.Nipc:
            case ApplicationType.Nicis:
            case ApplicationType.Nito:
                return 'ni';
            case ApplicationType.VacPharm:
                return 'vac-pharm';
            default:
                return '';
        }
    }

    getStartPage(type: ApplicationType = this.type): string {
        switch (type) {
            case ApplicationType.EhcRen:
                return 'wizard/person';

            case ApplicationType.Alap:
                return 'wizard/applicant-person';

            case ApplicationType.AlapExtr:
                return 'wizard/applicant-person-extr';

                case ApplicationType.Ehc:
            case ApplicationType.Ehc90Days:
                return 'wizard/' + this.getUrlForTypeAndStep(ApplicationType.Ehc, null, true);

            case ApplicationType.LegPerson:
                return 'wizard/person';
            case ApplicationType.LegSponsorship:
            case ApplicationType.Nito:
                return 'wizard/sponsorship';

            case ApplicationType.VacPharm:
                return 'wizard/person';

            case ApplicationType.Lia:
            case ApplicationType.LiaRen:
            case ApplicationType.Lia90Days:
            case ApplicationType.LiaBrg:
            case ApplicationType.Rla:
            case ApplicationType.Tla:
            case ApplicationType.Slap:
            case ApplicationType.SlapRen:
            case ApplicationType.Cla:
            case ApplicationType.ClaRen:
            case ApplicationType.Slas:
            case ApplicationType.SlasRen:
            case ApplicationType.Ola:
            case ApplicationType.OlaRen:
            case ApplicationType.ClaEhc:
            case ApplicationType.ClaEhcRen:
            case ApplicationType.Abo:
            case ApplicationType.Sub:
            case ApplicationType.Mut:
            case ApplicationType.Cogs:
            case ApplicationType.Ndn:
            case ApplicationType.Npf:
            default:
                return 'wizard';
        }
    }

    static getWizardStepTitle(type: ApplicationType, step: number, defaultTitle: string): string {
        switch (type) {
            case ApplicationType.AlapExtr:
                return AlaService.getWizardStepTitle(type, step, defaultTitle);
            default:
                return defaultTitle;
        }
    }

    getFullStartPageUrl(): string {
        const url = this.getFullCaseUrl() + this.getStartPage();
        return url;
    }

}
